import { useCallback, useState } from "react";

/**
 * The portal session: the bearer `session` token, its setter and `clear`.
 *
 * The token lives in `localStorage` under the portal's `storageKey`, so the
 * session survives a tab close and is shared across tabs in the same browser,
 * as in the member portal's `usePortalSession`. The tradeoff: on a shared
 * machine the token persists until explicit sign-out or server-side expiry
 * instead of clearing when the tab closes. The portals have no OIDC; this
 * self-issued bearer is the only auth on their API calls, passed through
 * `apiFetch`'s `token` option.
 *
 * On an `ApiError` with `status === 401`, `usePortalApi` calls `clear()` and
 * the UI falls back to the login view.
 */
export interface PortalSessionState {
  session: string | null;
  setSession: (token: string) => void;
  clear: () => void;
}

/**
 * Backing store for the session token. Consumed once at the provider root; the
 * rest of the app reads it through `usePortalSession` (the context consumer),
 * so there is a single source of truth and a 401 clears it everywhere.
 * `storageKey` is read on every write, but the initial value is read once at
 * mount, so the key must not change for the provider's lifetime.
 */
export function usePortalSessionStore(storageKey: string): PortalSessionState {
  const [session, setSessionState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(storageKey);
    } catch {
      // localStorage can throw in privacy modes / sandboxed iframes.
      return null;
    }
  });

  const setSession = useCallback(
    (token: string): void => {
      try {
        localStorage.setItem(storageKey, token);
      } catch {
        // Ignore a persistence failure and keep the token in memory for this tab.
      }
      setSessionState(token);
    },
    [storageKey],
  );

  const clear = useCallback((): void => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Ignore.
    }
    setSessionState(null);
  }, [storageKey]);

  return { session, setSession, clear };
}
